import { Alert, Button, cardClass, fieldClass, selectClass } from '@ledgerloom/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { propose } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { isAdmin, useSession } from '../session';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ErrorBanner, Icon, Loading, outcomeMessage, outcomeTone, useToast } from '../components/ui';
import { keyFrom, type ContactClass, type Fields, type Grade, type Lifecycle } from '../contactSettings';
import { SettingsLayout } from './Admin';

// Settings, Contacts (docs/design/contacts.md, Contact settings): the
// workspace's contact classes, lifecycles, and grades. Everyone sees them; an
// admin changes them, each section a change set another admin signs unless the
// workspace's sign-off policy lets an admin change that section alone. Keys come
// from the name when something is added and never change. Patterns:
// navigation/sidebar (the Settings menu), data-display/list-view,
// forms/text-field, forms/selection-input, content-management/modal.

type Draft<T> = T & { isNew?: boolean };
const REASON = 'Contact settings from Settings';

export function ContactSettings() {
  usePageTitle('Contact settings');
  const { me } = useSession();
  const admin = isAdmin(me);
  const fields = useApi<Fields>('/v1/contact-fields');
  const tenant = useApi<{ signoff_policy: { self_sign: string[] } }>(admin ? '/v1/tenant' : null);
  const f = fields.data;
  return (
    <SettingsLayout title="Contacts" lead="Classes, the lifecycles they follow, and grades. Every contact has one class; its class decides the stages it moves through.">
      {!admin && <Alert role="note" tone="info">Only an admin changes these. Ask one if something should change.</Alert>}
      {admin && tenant.data && <Alert role="note" tone="info">{signoffNote(tenant.data.signoff_policy.self_sign)}</Alert>}
      <ErrorBanner error={fields.error} />
      {!f ? <Loading label="Loading contact settings" /> : <>
        <Classes fields={f} admin={admin} onSaved={() => void fields.reload()} />
        <Lifecycles fields={f} admin={admin} onSaved={() => void fields.reload()} />
        <Grades fields={f} admin={admin} onSaved={() => void fields.reload()} />
      </>}
    </SettingsLayout>
  );
}

// What happens when an admin saves a section, from the sign-off policy (Settings, General).
const SECTIONS: Array<[string, string]> = [['config.contact_classes', 'classes'], ['config.contact_lifecycle', 'lifecycles'], ['config.contact_grades', 'grades']];
function signoffNote(alone: string[]): string {
  const own = SECTIONS.filter(([s]) => alone.includes(s)).map(([, name]) => name);
  if (!own.length) return 'Each section is proposed as a change set. Another admin signs it before it applies.';
  if (own.length === SECTIONS.length) return 'Your changes apply when you save them. The sign-off policy lets an admin change these alone, and each change is logged with your name.';
  return `Changes to ${own.join(' and ')} apply when you save them. Another admin signs the rest before they apply.`;
}

// One section's propose step: the outcome as a toast, the reason when refused.
function useProposal(onSaved: () => void) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const send = async (schema: string, payload: Record<string, unknown>, saved: string) => {
    setBusy(true); setError(null);
    try {
      const out = await propose(schema, { reason: REASON, ...payload });
      if (out.state === 'rejected') { setError(new Error(outcomeMessage(out))); return false; }
      toast(out.state === 'pending_approval' ? 'Proposed: another admin signs it before it applies' : outcomeMessage(out, saved), outcomeTone(out));
      onSaved();
      return true;
    } catch (e) { setError(e); return false; } finally { setBusy(false); }
  };
  return { busy, error, send };
}

function Section({ id, title, lead, children }: { id: string; title: string; lead: string; children: ReactNode }) {
  return (
    <section className={cardClass} aria-labelledby={id}>
      <div className="card-head"><div><h2 id={id}>{title}</h2><div className="faint">{lead}</div></div></div>
      <div className="card-pad stack" style={{ gap: 12 }}>{children}</div>
    </section>
  );
}

function Actions({ dirty, busy, admin, label, onSave, onReset }: { dirty: boolean; busy: boolean; admin: boolean; label: string; onSave: () => void; onReset: () => void }) {
  if (!admin) return null;
  return (
    <div className="row">
      <Button type="button" size="sm" disabled={!dirty || busy} onClick={onSave}>{busy ? 'Proposing…' : label}</Button>
      {dirty && <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onReset}>Discard changes</Button>}
    </div>
  );
}

// Keys for what is new, made from names, never clashing with a key already kept.
function withKeys<T extends { key: string; isNew?: boolean }>(items: T[], nameOf: (t: T) => string): Array<T & { key: string }> {
  const taken = items.filter((t) => !t.isNew).map((t) => t.key);
  return items.map((t) => {
    if (!t.isNew) return t;
    const key = keyFrom(nameOf(t), taken);
    taken.push(key);
    return { ...t, key };
  });
}

const move = <T,>(list: T[], i: number, by: number): T[] => { const next = [...list]; const [x] = next.splice(i, 1); next.splice(i + by, 0, x!); return next; };

function Classes({ fields, admin, onSaved }: { fields: Fields; admin: boolean; onSaved: () => void }) {
  const [rows, setRows] = useState<Array<Draft<ContactClass>>>(fields.classes);
  useEffect(() => { setRows(fields.classes); }, [fields.classes]);
  const { busy, error, send } = useProposal(onSaved);
  const dirty = JSON.stringify(rows) !== JSON.stringify(fields.classes);
  const put = (i: number, patch: Partial<ContactClass>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = () => {
    const name = 'New class';
    setRows([...rows, { key: keyFrom(name, rows.map((r) => r.key)), name, description: null, lifecycle: fields.lifecycles[0]!.key, is_default: false, isNew: true }]);
  };
  const save = () => void send('config.contact_classes', {
    classes: withKeys(rows, (r) => r.name).map((r) => ({ key: r.key, name: r.name.trim(), ...(r.description?.trim() ? { description: r.description.trim() } : {}), lifecycle: r.lifecycle, is_default: r.is_default })),
  }, 'Classes saved');
  const blank = rows.some((r) => !r.name.trim());
  return (
    <Section id="classes-h" title="Contact classes" lead="A contact has exactly one class. New contacts get the default.">
      <ErrorBanner error={error} />
      <ul className="settings-list">
        {rows.map((r, i) => (
          <li key={r.isNew ? `new-${i}` : r.key} className="settings-row">
            <div className="settings-fields">
              <label className="field-inline"><span className="small muted">Name</span>
                <input className={`${fieldClass} h-9`} value={r.name} maxLength={80} disabled={!admin} onChange={(e) => put(i, { name: e.target.value })} />
              </label>
              <label className="field-inline grow"><span className="small muted">Description</span>
                <input className={`${fieldClass} h-9`} value={r.description ?? ''} maxLength={500} disabled={!admin} placeholder="Optional" onChange={(e) => put(i, { description: e.target.value })} />
              </label>
              <label className="field-inline"><span className="small muted">Lifecycle</span>
                <select className={`${selectClass} h-9 w-auto`} value={r.lifecycle} disabled={!admin} onChange={(e) => put(i, { lifecycle: e.target.value })}>
                  {fields.lifecycles.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
                </select>
              </label>
              <label className="row small" style={{ gap: 6 }}>
                <input type="radio" name="default-class" checked={r.is_default} disabled={!admin} onChange={() => setRows(rows.map((x, j) => ({ ...x, is_default: j === i })))} />
                Default for new contacts
              </label>
            </div>
            {admin && (
              <div className="row settings-row-actions" style={{ gap: 4 }}>
                <Button type="button" variant="ghost" size="sm" aria-label={`Move ${r.name} up`} disabled={i === 0} onClick={() => setRows(move(rows, i, -1))}>↑</Button>
                <Button type="button" variant="ghost" size="sm" aria-label={`Move ${r.name} down`} disabled={i === rows.length - 1} onClick={() => setRows(move(rows, i, 1))}>↓</Button>
                <Button type="button" variant="ghost" size="sm" disabled={r.is_default || rows.length === 1} title={r.is_default ? 'Choose another default first' : undefined}
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove<span className="sr-only"> {r.name}</span></Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {admin && <div><Button type="button" variant="ghost" size="sm" onClick={add}><Icon name="plus" />Add a class</Button></div>}
      {blank && <p className="small field-error" style={{ margin: 0 }}>Every class needs a name.</p>}
      <p className="faint small" style={{ margin: 0 }}>A class that contacts still have cannot be removed: move them to another class first. Moving a class to another lifecycle clears the stages its contacts hold that the new lifecycle lacks.</p>
      <Actions dirty={dirty} busy={busy || blank} admin={admin} label="Propose classes" onSave={save} onReset={() => setRows(fields.classes)} />
    </Section>
  );
}

function Lifecycles({ fields, admin, onSaved }: { fields: Fields; admin: boolean; onSaved: () => void }) {
  const [added, setAdded] = useState<Lifecycle | null>(null);
  const used = (key: string) => fields.classes.filter((c) => c.lifecycle === key).map((c) => c.name);
  return (
    <Section id="lifecycles-h" title="Lifecycles" lead="The stages a contact moves through, in order. Each class follows one lifecycle.">
      {fields.lifecycles.map((l) => <LifecycleEditor key={l.key} lifecycle={l} classes={used(l.key)} admin={admin} onSaved={onSaved} />)}
      {added && <LifecycleEditor lifecycle={added} isNew classes={[]} taken={fields.lifecycles.map((l) => l.key)} admin={admin} onSaved={() => { setAdded(null); onSaved(); }} onCancel={() => setAdded(null)} />}
      {admin && !added && (
        <div><Button type="button" variant="ghost" size="sm" onClick={() => setAdded({ key: '', name: 'New lifecycle', stages: [{ key: 'new', name: 'New' }, { key: 'active', name: 'Active' }] })}>
          <Icon name="plus" />Add a lifecycle
        </Button></div>
      )}
    </Section>
  );
}

function LifecycleEditor({ lifecycle, isNew, classes, taken = [], admin, onSaved, onCancel }: {
  lifecycle: Lifecycle; isNew?: boolean; classes: string[]; taken?: string[]; admin: boolean; onSaved: () => void; onCancel?: () => void;
}) {
  const [name, setName] = useState(lifecycle.name);
  const [stages, setStages] = useState<Array<Draft<{ key: string; name: string }>>>(isNew ? lifecycle.stages.map((s) => ({ ...s, isNew: true })) : lifecycle.stages);
  const [archiving, setArchiving] = useState(false);
  useEffect(() => { setName(lifecycle.name); if (!isNew) setStages(lifecycle.stages); }, [lifecycle]); // eslint-disable-line react-hooks/exhaustive-deps
  const { busy, error, send } = useProposal(onSaved);
  const dirty = isNew || name !== lifecycle.name || JSON.stringify(stages) !== JSON.stringify(lifecycle.stages);
  const put = (i: number, n: string) => setStages(stages.map((s, j) => (j === i ? { ...s, name: n } : s)));
  const keyed = () => withKeys(stages, (s) => s.name).map((s) => ({ key: s.key, name: s.name.trim() }));
  const payload = (archived = false) => ({ key: isNew ? keyFrom(name, taken) : lifecycle.key, name: name.trim(), stages: keyed(), ...(archived ? { archived: true } : {}) });
  const blank = !name.trim() || stages.some((s) => !s.name.trim());
  const headId = `lc-${lifecycle.key || 'new'}-h`;
  return (
    <div className="lifecycle-card" role="group" aria-labelledby={headId}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 id={headId} className="sr-only">{isNew ? 'New lifecycle' : `Lifecycle ${lifecycle.name}`}</h3>
        <label className="field-inline"><span className="small muted">Lifecycle name</span>
          <input className={`${fieldClass} h-9`} value={name} maxLength={80} disabled={!admin} onChange={(e) => setName(e.target.value)} />
        </label>
        <span className="faint small">{classes.length ? `Followed by ${classes.join(', ')}` : isNew ? 'Not followed by any class yet' : 'No class follows it'}</span>
      </div>
      <ErrorBanner error={error} />
      <ol className="settings-list stages">
        {stages.map((s, i) => (
          <li key={s.isNew ? `new-${i}` : s.key} className="settings-row">
            <span className="stage-n faint small" aria-hidden="true">{i + 1}</span>
            <label className="grow"><span className="sr-only">Stage {i + 1} name</span>
              <input className={`${fieldClass} h-9`} value={s.name} maxLength={80} disabled={!admin} onChange={(e) => put(i, e.target.value)} />
            </label>
            {admin && (
              <div className="row settings-row-actions" style={{ gap: 4 }}>
                <Button type="button" variant="ghost" size="sm" aria-label={`Move ${s.name} up`} disabled={i === 0} onClick={() => setStages(move(stages, i, -1))}>↑</Button>
                <Button type="button" variant="ghost" size="sm" aria-label={`Move ${s.name} down`} disabled={i === stages.length - 1} onClick={() => setStages(move(stages, i, 1))}>↓</Button>
                <Button type="button" variant="ghost" size="sm" disabled={stages.length === 1} onClick={() => setStages(stages.filter((_, j) => j !== i))}>Remove<span className="sr-only"> {s.name}</span></Button>
              </div>
            )}
          </li>
        ))}
      </ol>
      {admin && <div><Button type="button" variant="ghost" size="sm" disabled={stages.length >= 20} onClick={() => setStages([...stages, { key: '', name: 'New stage', isNew: true }])}><Icon name="plus" />Add a stage</Button></div>}
      {blank && <p className="small field-error" style={{ margin: 0 }}>The lifecycle and each stage need a name.</p>}
      {admin && (
        <div className="row">
          <Button type="button" size="sm" disabled={!dirty || busy || blank} onClick={() => void send('config.contact_lifecycle', payload(), 'Lifecycle saved')}>{busy ? 'Proposing…' : isNew ? 'Propose this lifecycle' : 'Propose changes'}</Button>
          {isNew ? <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
            : dirty ? <Button type="button" variant="ghost" size="sm" onClick={() => { setName(lifecycle.name); setStages(lifecycle.stages); }}>Discard changes</Button>
            : <Button type="button" variant="ghost" size="sm" disabled={classes.length > 0} title={classes.length ? 'A class still follows it' : undefined} onClick={() => setArchiving(true)}>Archive lifecycle…</Button>}
        </div>
      )}
      <ConfirmDialog open={archiving} title={`Archive ${lifecycle.name}?`} confirmLabel="Propose archiving" tone="danger" busy={busy}
        onConfirm={() => { void send('config.contact_lifecycle', payload(true), 'Lifecycle archived').then(() => setArchiving(false)); }} onCancel={() => setArchiving(false)}>
        <p style={{ margin: 0 }}>It leaves this list and no class can follow it. It is kept, not deleted.</p>
      </ConfirmDialog>
    </div>
  );
}

function Grades({ fields, admin, onSaved }: { fields: Fields; admin: boolean; onSaved: () => void }) {
  const [rows, setRows] = useState<Array<Draft<Grade>>>(fields.grades);
  useEffect(() => { setRows(fields.grades); }, [fields.grades]);
  const { busy, error, send } = useProposal(onSaved);
  const dirty = JSON.stringify(rows) !== JSON.stringify(fields.grades);
  const put = (i: number, patch: Partial<Grade>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const codes = rows.map((r) => r.key.trim());
  const twice = codes.find((c, i) => codes.indexOf(c) !== i);
  const bad = rows.some((r) => !/^[A-Za-z0-9][A-Za-z0-9_+-]{0,19}$/.test(r.key.trim()) || !r.label.trim());
  return (
    <Section id="grades-h" title="Grades" lead="How well a contact fits, highest first. The code shows on lists and boards.">
      <ErrorBanner error={error} />
      <ol className="settings-list">
        {rows.map((r, i) => (
          <li key={r.isNew ? `new-${i}` : r.key} className="settings-row">
            <div className="settings-fields">
              <label className="field-inline"><span className="small muted">Code</span>
                {r.isNew ? <input className={`${fieldClass} h-9`} style={{ width: 90 }} value={r.key} maxLength={20} onChange={(e) => put(i, { key: e.target.value })} />
                  : <span className="grade-code" title="A grade's code never changes">{r.key}</span>}
              </label>
              <label className="field-inline"><span className="small muted">Name</span>
                <input className={`${fieldClass} h-9`} value={r.label} maxLength={80} disabled={!admin} onChange={(e) => put(i, { label: e.target.value })} />
              </label>
              <label className="field-inline grow"><span className="small muted">Description</span>
                <input className={`${fieldClass} h-9`} value={r.description ?? ''} maxLength={500} disabled={!admin} placeholder="Optional" onChange={(e) => put(i, { description: e.target.value })} />
              </label>
            </div>
            {admin && (
              <div className="row settings-row-actions" style={{ gap: 4 }}>
                <Button type="button" variant="ghost" size="sm" aria-label={`Move ${r.key || 'new grade'} up`} disabled={i === 0} onClick={() => setRows(move(rows, i, -1))}>↑</Button>
                <Button type="button" variant="ghost" size="sm" aria-label={`Move ${r.key || 'new grade'} down`} disabled={i === rows.length - 1} onClick={() => setRows(move(rows, i, 1))}>↓</Button>
                <Button type="button" variant="ghost" size="sm" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove<span className="sr-only"> grade {r.key}</span></Button>
              </div>
            )}
          </li>
        ))}
      </ol>
      {admin && <div><Button type="button" variant="ghost" size="sm" disabled={rows.length >= 20} onClick={() => setRows([...rows, { key: '', label: '', description: null, isNew: true }])}><Icon name="plus" />Add a grade</Button></div>}
      {(twice || bad) && <p className="small field-error" style={{ margin: 0 }}>{twice ? `The code ${twice} is used twice.` : 'Each grade needs a code (letters, digits, + or -) and a name.'}</p>}
      <p className="faint small" style={{ margin: 0 }}>A grade that contacts still have cannot be removed.</p>
      <Actions dirty={dirty} busy={busy || !!twice || bad} admin={admin} label="Propose grades" onReset={() => setRows(fields.grades)}
        onSave={() => void send('config.contact_grades', { grades: rows.map((r) => ({ key: r.key.trim(), label: r.label.trim(), ...(r.description?.trim() ? { description: r.description.trim() } : {}) })) }, 'Grades saved')} />
    </Section>
  );
}
